/**
 * Redux slice (src/store/slices/teacherWalletSlice.js).
 *
 * Data flow (typical):
 * - UI dispatches actions/thunks from `src/store/actions/*`.
 * - `reducers` handles synchronous state updates.
 * - `extraReducers` reacts to thunk pending/fulfilled/rejected actions.
 */

import { createSlice } from "@reduxjs/toolkit";
import {
  setTeacherEarningRate,
  creditLessonEarnings,
  getTeacherEarningsBalance,
  getTeacherEarningsHistory,
  fetchTeacherWalletData,
  fetchTeacherWalletTransactions,
  fetchTeacherWallet,
  fetchTeacherWalletStats,
} from "../actions/teacherWallet";

/**
 * Slice initial state.
 *
 * Keep this shape stable because it is consumed across many selectors/components.
 */
const initialState = {
  balance: 0,
  earningRate: 0,
  currency: 'PLN',
  earningsHistory: [],
  transactions: [],
  pagination: {
    total: 0,
    page: 1,
    pages: 1
  },
  stats: {
    totalEarned: 0,
    totalWithdrawn: 0,
    currentBalance: 0,
    completedLessons: 0,
    recentEarnings: 0,
    totalEarnings: 0,
    averagePerLesson: 0
  },
  loading: false,
  statsLoading: false,
  error: null,
  success: false,
  lastFetched: null
};

/**
 * RTK slice definition: teacherEarningsSlice.
 */
const teacherEarningsSlice = createSlice({
  name: "teacherEarnings",
  initialState,
  reducers: {
    /**
     * Reducer: clearEarningsState.
     */
    clearEarningsState: (state) => {
      state.error = null;
      state.success = false;
    },
    /**
     * Reducer: setHistoryPage.
     */
    setHistoryPage: (state, action) => {
      state.pagination.page = action.payload;
    }
  },
  /**
   * Reducer: extraReducers.
   */
  extraReducers: (builder) => {
    builder
      // Set Earnings Rate
      .addCase(fetchTeacherWalletData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // extraReducers handler: fetchTeacherWalletData.fulfilled
      .addCase(fetchTeacherWalletData.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.error = null;
        
        if (action.payload && action.payload.walletBalance !== undefined) {
          state.balance = action.payload.walletBalance;
        }
        
        if (action.payload && action.payload.summary) {
          state.stats = {
            totalEarned: action.payload.summary.totalEarnings || 0,
            totalWithdrawn: 0,
            currentBalance: action.payload.walletBalance || 0,
            completedLessons: action.payload.summary.completedBookings || 0
          };
        }
        
        // If the API returns earnings history, update it
        if (action.payload && action.payload.bookings) {
          state.earningsHistory = action.payload.bookings
            .filter(booking => String(booking.status || '').toLowerCase() === 'completed')
            .map(booking => ({
              ...booking,
              amount: booking.earnings?.amount || 0,
              timestamp: booking.completedAt || booking.startTime || new Date(),
              type: 'credit'
            }));
        }
      })
      // extraReducers handler: fetchTeacherWalletData.rejected
      .addCase(fetchTeacherWalletData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error?.message || 'Failed to fetch wallet data';
        state.success = false;
      })
      // extraReducers handler: setTeacherEarningRate.pending
      .addCase(setTeacherEarningRate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // extraReducers handler: setTeacherEarningRate.fulfilled
      .addCase(setTeacherEarningRate.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.earningRate = action.payload.data.teacher.earningsPerLesson;
        state.currency = action.payload.data.teacher.earningsCurrency;
      })
      // extraReducers handler: setTeacherEarningRate.rejected
      .addCase(setTeacherEarningRate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // Credit Lesson Earnings
      .addCase(creditLessonEarnings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // extraReducers handler: creditLessonEarnings.fulfilled
      .addCase(creditLessonEarnings.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.balance = action.payload.data.wallet.balance;
        if (state.earningsHistory.length > 0) {
          state.earningsHistory.unshift(action.payload.data.wallet.transactions[0]);
        }
        state.stats.totalEarned += action.payload.data.wallet.transactions[0].amount;
        state.stats.completedLessons += 1;
      })
      // extraReducers handler: creditLessonEarnings.rejected
      .addCase(creditLessonEarnings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // Get Balance
      .addCase(getTeacherEarningsBalance.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // extraReducers handler: getTeacherEarningsBalance.fulfilled
      .addCase(getTeacherEarningsBalance.fulfilled, (state, action) => {
        state.loading = false;
        state.balance = action.payload.data.wallet.balance;
        state.stats = action.payload.data.stats;
      })
      // extraReducers handler: getTeacherEarningsBalance.rejected
      .addCase(getTeacherEarningsBalance.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // Get History
      .addCase(getTeacherEarningsHistory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // extraReducers handler: getTeacherEarningsHistory.fulfilled
      .addCase(getTeacherEarningsHistory.fulfilled, (state, action) => {
        state.loading = false;
        state.earningsHistory = action.payload.data.transactions;
        state.pagination = action.payload.data.pagination;
      })
      // extraReducers handler: getTeacherEarningsHistory.rejected
      .addCase(getTeacherEarningsHistory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      
      // Fetch Teacher Wallet Transactions
      .addCase(fetchTeacherWalletTransactions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // extraReducers handler: fetchTeacherWalletTransactions.fulfilled
      .addCase(fetchTeacherWalletTransactions.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.error = null;

        const data = action.payload?.data || {};
        state.balance = data.balance || 0;
        state.transactions = data.transactions || [];
        state.earningsHistory = data.transactions || [];
        state.pagination = data.pagination || state.pagination;
      })
      // extraReducers handler: fetchTeacherWalletTransactions.rejected
      .addCase(fetchTeacherWalletTransactions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error?.message || 'Failed to fetch wallet transactions';
        state.success = false;
      })
      
      // Fetch Teacher Wallet (new endpoint)
      .addCase(fetchTeacherWallet.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // extraReducers handler: fetchTeacherWallet.fulfilled
      .addCase(fetchTeacherWallet.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.error = null;
        state.lastFetched = Date.now();
        
        if (action.payload && action.payload.success) {
          const data = action.payload.data;
          state.balance = data.balance || 0;
          state.transactions = data.transactions || [];
          
          if (data.stats) {
            state.stats = {
              ...state.stats,
              ...data.stats,
              currentBalance: data.balance || 0
            };
          }
        }
      })
      // extraReducers handler: fetchTeacherWallet.rejected
      .addCase(fetchTeacherWallet.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error?.message || 'Failed to fetch wallet';
        state.success = false;
      })
      
      // Fetch Teacher Wallet Stats
      .addCase(fetchTeacherWalletStats.pending, (state) => {
        state.statsLoading = true;
        state.error = null;
      })
      // extraReducers handler: fetchTeacherWalletStats.fulfilled
      .addCase(fetchTeacherWalletStats.fulfilled, (state, action) => {
        state.statsLoading = false;
        state.success = true;
        state.error = null;
        
        if (action.payload && action.payload.success) {
          state.stats = {
            ...state.stats,
            ...action.payload.data
          };
        }
      })
      // extraReducers handler: fetchTeacherWalletStats.rejected
      .addCase(fetchTeacherWalletStats.rejected, (state, action) => {
        state.statsLoading = false;
        state.error = action.payload || action.error?.message || 'Failed to fetch wallet stats';
        state.success = false;
      });
  }
});

export const { clearEarningsState, setHistoryPage } = teacherEarningsSlice.actions;

// Selectors
/**
 * Selector: return the teacher earnings wallet data in a UI-friendly shape.
 *
 * This selector groups fields used by dashboard components (balance, transactions, stats, flags).
 */
export const selectWalletData = (state) => ({
  balance: state.teacherEarnings.balance,
  transactions: state.teacherEarnings.transactions,
  stats: state.teacherEarnings.stats,
  loading: state.teacherEarnings.loading,
  statsLoading: state.teacherEarnings.statsLoading,
  error: state.teacherEarnings.error,
  lastFetched: state.teacherEarnings.lastFetched
});

export default teacherEarningsSlice.reducer;
